import { useEffect, useRef, useState } from "react";
import { imageSources, lqipSource } from "@/lib/images";

/**
 * Responsive, lazy-loaded image with LQIP (Low Quality Image Placeholder).
 *
 * context:
 *  - "card"  → always serves the 600px thumbnail (lists, cards, filter results)
 *  - "full"  → mobile: thumbnail · tablet: medium · desktop: large (detail & gallery)
 *
 * A tiny blurred placeholder is shown instantly; once the real image loads it
 * fades + blurs out in ~280ms. The placeholder sits in a wrapper that fills its
 * parent, so layout/design is unchanged (the inner <img> keeps its className,
 * object-fit and hover transforms).
 *
 * Set placeholder={false} to render a bare <picture> (used for the fullscreen
 * viewer where object-contain differs from the cover-based placeholder).
 */
export default function SmartImage({
  src,
  alt = "",
  className = "",
  context = "full",
  eager = false,
  placeholder = true,
  onClick,
  ...rest
}) {
  const { thumb, medium, large } = imageSources(src);
  const lqip = placeholder ? lqipSource(src) : null;
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    setLoaded(false);
    // Image may already be cached before onLoad attaches
    if (imgRef.current && imgRef.current.complete && imgRef.current.naturalWidth > 0) {
      setLoaded(true);
    }
  }, [src]);

  const imgProps = {
    ref: imgRef,
    alt,
    className,
    onClick,
    onLoad: () => setLoaded(true),
    loading: eager ? "eager" : "lazy",
    decoding: "async",
    ...rest,
  };

  const picture =
    context === "card" ? (
      <picture className="contents">
        <img src={thumb} {...imgProps} />
      </picture>
    ) : (
      <picture className="contents">
        <source media="(max-width: 640px)" srcSet={thumb} />
        <source media="(max-width: 1024px)" srcSet={medium} />
        <img src={large} {...imgProps} />
      </picture>
    );

  if (!lqip) return picture;

  return (
    <span className="relative block w-full h-full overflow-hidden">
      {picture}
      <span
        aria-hidden="true"
        data-testid="lqip-placeholder"
        className="absolute inset-0 bg-cover bg-center pointer-events-none"
        style={{
          backgroundImage: `url("${lqip}")`,
          filter: "blur(16px)",
          transform: "scale(1.1)",
          opacity: loaded ? 0 : 1,
          transition: "opacity 280ms ease",
          willChange: "opacity",
        }}
      />
    </span>
  );
}
